import type { SiteCreatePayload } from "@/customTypings/site/Site";
import { authHeader, handleResponse } from "..";

export const SITE_CREATE_CONFLICT = {
    duplicateActive: "site.duplicateActive",
    deletedRestoreAvailable: "site.deletedRestoreAvailable",
    duplicateAmbiguous: "site.duplicateAmbiguous",
} as const;

export type SiteDeletedRestoreAvailableConflict = {
    code: string;
    message: string;
    existingId: string;
    existingSiteName: string;
};

export function isSiteDeletedRestoreAvailableConflict(
    value: unknown,
    expectedCode: string
): value is SiteDeletedRestoreAvailableConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as SiteDeletedRestoreAvailableConflict).code === expectedCode &&
        typeof (value as SiteDeletedRestoreAvailableConflict).existingId === "string"
    );
}

export class SiteCreateConflictError extends Error {
    readonly conflict: SiteDeletedRestoreAvailableConflict;

    constructor(conflict: SiteDeletedRestoreAvailableConflict) {
        super(conflict.message);
        this.name = "SiteCreateConflictError";
        this.conflict = conflict;
    }
}

export type SiteRestoreDeletedPayload = SiteCreatePayload & { id: string };

/** POST create; throws SiteCreateConflictError on structured 409 restore-available. */
export async function postSiteCreate<TSite>(createUrl: string, payload: SiteCreatePayload): Promise<TSite> {
    const response = await fetch(createUrl, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        /* non-JSON */
    }

    if (
        response.status === 409 &&
        isSiteDeletedRestoreAvailableConflict(data, SITE_CREATE_CONFLICT.deletedRestoreAvailable)
    ) {
        throw new SiteCreateConflictError(data);
    }

    return handleResponse({
        text: async () => text,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
    }) as Promise<TSite>;
}
